import React, { useEffect, useState, useRef, useMemo } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  switchMap,
  tap,
  map,
  filter,
  Subject,
  takeUntil,
  take,
  catchError,
} from "rxjs";
import Modal from "../../../atoms/Modal";
import PublicationSearchResults from "../PublicationSearchResults";
import apiClient from "../../../../services/apiClient";
import { getSearchEntity } from "../../../../state/selectors/Publications.selectors";
import Article from "../../models/Article";
import SourceEntity from "../../models/SourceEntity";
import rxErrorHandler from "../../../../common/helpers/rxErrorHandler";
import PubmedArticle from "../PubmedArticle";
import Loader from "../../../Loader";

function PublicationLinks({
  ids: pubmedIds,
  itemDesc,
  className,
  isModal,
  children,
  searchEntity,
  sourceEntity,
  forceList,
  ...rest
}) {
  const ids = useMemo(() => {
    if (!Array.isArray(pubmedIds)) return [];
    const fixedIds = pubmedIds.map((x) => String(x));
    const uniqueIds = new Set(fixedIds).values();
    return Array.from(uniqueIds).filter(Boolean);
  }, [pubmedIds]);
  const search$ = useRef(new Subject()).current;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [article, setArticle] = useState(null);
  const len = ids?.length;
  const firstId = ids?.[0];
  const txt = len === 1 ? `PMID:${firstId}` : `${len} publications`;
  const isSingle = ids.length === 1 && !forceList;

  useEffect(() => {
    const destroy$ = new Subject();

    search$
      .pipe(
        take(1),
        filter(() => ids?.length === 1),
        map(() => ids?.[0]),
        tap(() => setIsLoading(true)),
        switchMap((pubId) => apiClient.getPublication(pubId)),
        map((x) => (x?.results?.length ? x.results[0] : null)),
        takeUntil(destroy$),
        tap((pub) => setArticle(new Article(pub))),
        catchError(rxErrorHandler(() => setError(true))),
        tap(() => setIsLoading(false))
      )
      .subscribe();

    if (!isModal) search$.next();

    return () => destroy$.next();
  }, []);

  if (!ids?.length) return <span>-</span>;
  if (error) return <span>Error loading {itemDesc}</span>;
  const entity = sourceEntity || searchEntity || new SourceEntity({});

  const content = (
    <div className="tw-min-h-96">
      {isSingle && article ? (
        <div className="tw-py-8">
          <PubmedArticle article={article} />
        </div>
      ) : null}

      {!isSingle ? (
        <PublicationSearchResults
          shouldShowTags={true}
          shouldShowContributions={false}
          shouldShowAbstract={false}
          {...rest}
          showTimeline={false}
          isModal={isModal}
          sourceEntity={entity.clone().setPubIds(ids)}
        />
      ) : null}

      {isLoading ? <Loader /> : null}
    </div>
  );

  return isModal ? (
    <>
      <div
        onClick={() => {
          setIsModalOpen(true);
          search$.next();
        }}
        onMouseMove={() => search$.next()}
        className={className}
        data-tooltip={`Open ${txt}`}
        data-tooltip-class="genome-browser-small-tooltip"
      >
        {children || txt}
      </div>

      <Modal
        isOpen={isModalOpen}
        containerClassName="prevent-tooltip-close"
        className="tw-w-90screen tw-max-w-screen-xl"
        onRequestClose={() => setIsModalOpen(false)}
      >
        <div className="tw-mr-20 tw-ml-6">{content}</div>
      </Modal>
    </>
  ) : (
    content
  );
}

const pubmedIdsType = PropTypes.arrayOf(
  PropTypes.oneOfType([PropTypes.number, PropTypes.string])
);

PublicationLinks.propTypes = {
  forceList: PropTypes.bool,
  searchEntity: PropTypes.instanceOf(SourceEntity),
  sourceEntity: PropTypes.instanceOf(SourceEntity),
  children: PropTypes.node,
  isModal: PropTypes.bool,
  className: PropTypes.string,
  itemDesc: PropTypes.string,
  ids: pubmedIdsType,
};

PublicationLinks.defaultProps = {
  forceList: false,
  searchEntity: null,
  sourceEntity: null,
  children: null,
  ids: [],
  isModal: true,
  className:
    "tw-cursor-pointer tw-inline-block tw-text-primary hover:tw-underline tw-whitespace-nowrap",
  itemDesc: "publication",
};

const mapStateToProps = (state) => ({
  searchEntity: getSearchEntity(state),
});

export default connect(mapStateToProps)(PublicationLinks);
